/**
 * SQLite Context Provider
 * 
 * Provides global access to the local SQLite database for offline-first operations.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { 
  initSQLite, 
  isDatabaseReady, 
  query, 
  execute, 
  transaction,
  getSyncQueueStats,
  persistToIndexedDB,
  resetDatabase,
  getDatabaseSize
} from '@/lib/sqlite-local';
import { useRestaurantContext } from './RestaurantContext';
import { Database } from 'sql.js';

interface SyncStats {
  pending: number;
  syncing: number;
  failed: number;
  total: number;
}

interface SQLiteContextType {
  isReady: boolean;
  isLoading: boolean;
  error: Error | null;
  db: Database | null;
  // Query functions
  query: <T = Record<string, unknown>>(sql: string, params?: unknown[]) => T[];
  execute: (sql: string, params?: unknown[]) => void;
  transaction: (statements: Array<{ sql: string; params?: unknown[] }>) => void;
  // Sync status
  syncStats: SyncStats;
  refreshSyncStats: () => void;
  // Utilities
  persistNow: () => Promise<void>;
  resetDatabase: () => Promise<void>;
  getDatabaseSizeKB: () => number;
}

const SQLiteContext = createContext<SQLiteContextType | undefined>(undefined);

interface SQLiteProviderProps {
  children: ReactNode;
}

export function SQLiteProvider({ children }: SQLiteProviderProps) {
  const { activeRestaurant } = useRestaurantContext();
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [db, setDb] = useState<Database | null>(null);
  const [syncStats, setSyncStats] = useState<SyncStats>({ pending: 0, syncing: 0, failed: 0, total: 0 });

  // Initialize SQLite when restaurant is available
  useEffect(() => {
    if (!activeRestaurant) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[SQLiteContext] Initializing database...');
        const database = await initSQLite(activeRestaurant.id);
        
        if (cancelled) return;

        setDb(database);
        setIsReady(true);
        console.log('[SQLiteContext] Database ready');

        // Initial sync stats
        refreshSyncStatsInternal();
      } catch (err) {
        if (cancelled) return;
        console.error('[SQLiteContext] Initialization error:', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize SQLite'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [activeRestaurant?.id]);

  // Refresh sync stats
  const refreshSyncStatsInternal = useCallback(() => {
    if (!isDatabaseReady()) return;
    try {
      const stats = getSyncQueueStats();
      setSyncStats(stats);
    } catch (error) {
      console.error('[SQLiteContext] Error refreshing sync stats:', error);
    }
  }, []);

  const refreshSyncStats = useCallback(() => {
    refreshSyncStatsInternal();
  }, [refreshSyncStatsInternal]);

  // Periodic sync stats refresh
  useEffect(() => {
    if (!isReady) return;

    const interval = setInterval(refreshSyncStatsInternal, 5000);
    return () => clearInterval(interval);
  }, [isReady, refreshSyncStatsInternal]);

  // Wrapped query function with error handling
  const safeQuery = useCallback(<T = Record<string, unknown>>(sql: string, params: unknown[] = []): T[] => {
    if (!isReady) {
      console.warn('[SQLiteContext] Query attempted before database is ready');
      return [];
    }
    try {
      return query<T>(sql, params);
    } catch (error) {
      console.error('[SQLiteContext] Query error:', error);
      throw error;
    }
  }, [isReady]);

  // Wrapped execute function with error handling
  const safeExecute = useCallback((sql: string, params: unknown[] = []): void => {
    if (!isReady) {
      console.warn('[SQLiteContext] Execute attempted before database is ready');
      return;
    }
    try {
      execute(sql, params);
      // Refresh sync stats after mutations
      setTimeout(refreshSyncStatsInternal, 100);
    } catch (error) {
      console.error('[SQLiteContext] Execute error:', error);
      throw error;
    }
  }, [isReady, refreshSyncStatsInternal]);

  // Wrapped transaction function
  const safeTransaction = useCallback((statements: Array<{ sql: string; params?: unknown[] }>): void => {
    if (!isReady) {
      console.warn('[SQLiteContext] Transaction attempted before database is ready');
      return;
    }
    try {
      transaction(statements);
      setTimeout(refreshSyncStatsInternal, 100);
    } catch (error) {
      console.error('[SQLiteContext] Transaction error:', error);
      throw error;
    }
  }, [isReady, refreshSyncStatsInternal]);

  // Persist database now
  const persistNow = useCallback(async () => {
    await persistToIndexedDB();
  }, []);

  // Reset database
  const handleResetDatabase = useCallback(async () => {
    await resetDatabase();
    setIsReady(false);
    setDb(null);
    setSyncStats({ pending: 0, syncing: 0, failed: 0, total: 0 });
  }, []);

  // Get database size in KB
  const getDatabaseSizeKB = useCallback(() => {
    return Math.round(getDatabaseSize() / 1024);
  }, []);

  const value: SQLiteContextType = {
    isReady,
    isLoading,
    error,
    db,
    query: safeQuery,
    execute: safeExecute,
    transaction: safeTransaction,
    syncStats,
    refreshSyncStats,
    persistNow,
    resetDatabase: handleResetDatabase,
    getDatabaseSizeKB,
  };

  return (
    <SQLiteContext.Provider value={value}>
      {children}
    </SQLiteContext.Provider>
  );
}

export function useSQLiteContext() {
  const context = useContext(SQLiteContext);
  if (context === undefined) {
    throw new Error('useSQLiteContext must be used within a SQLiteProvider');
  }
  return context;
}

/**
 * Hook for checking if SQLite is ready
 */
export function useSQLiteReady(): boolean {
  const context = useContext(SQLiteContext);
  return context?.isReady ?? false;
}
